import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

// Import slices
import databaseReducer from './slices/databaseSlice';
import createCRReducer from './slices/createCRSlice';
import reviewCRReducer from './slices/reviewCRSlice';
import authReducer from './slices/authSlice';

// Persistence middleware for form state
const createFormPersistenceMiddleware = () => {
  return (store: any) => (next: any) => (action: any) => {
    const result = next(action);
    
    // Handle reset action - clear localStorage
    if (action.type?.includes('resetCreateCR')) {
      try {
        localStorage.removeItem('cr_form_state');
      } catch (error) {
        console.warn('Failed to clear persisted form state:', error);
      }
    }
    // Only persist form data actions for createCR slice
    else if (action.type?.startsWith('createCR/') && !action.type.includes('reset')) {
      const state = store.getState();
      try {
        localStorage.setItem('cr_form_state', JSON.stringify({
          form_data: state.createCR.form_data,
          workflow: state.createCR.workflow
        }));
      } catch (error) {
        console.warn('Failed to persist form state:', error);
      }
    }
    
    return result;
  };
};

// Load persisted form state
const loadPersistedFormState = () => {
  try {
    const persistedState = localStorage.getItem('cr_form_state');
    return persistedState ? JSON.parse(persistedState) : undefined;
  } catch (error) {
    console.warn('Failed to load persisted form state:', error);
    return undefined;
  }
};

// Helper function to clear persisted form state
export const clearPersistedFormState = () => {
  try {
    localStorage.removeItem('cr_form_state');
  } catch (error) {
    console.warn('Failed to clear persisted form state:', error);
  }
};

// Create preloaded state with persisted form data
const persistedFormState = loadPersistedFormState();
const preloadedState = persistedFormState ? {
  createCR: {
    ...createCRReducer(undefined, { type: '@@INIT' }),
    ...persistedFormState
  }
} : undefined;

export const store = configureStore({
  reducer: {
    auth: authReducer,
    database: databaseReducer,
    createCR: createCRReducer,
    reviewCR: reviewCRReducer,
  },
  preloadedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types for persistence
        ignoredActions: [
          'createCR/updateModuleData',
          'createCR/updateInverterData',
          'createCR/updateBatteryData',
          'createCR/updateStructuralData',
          'createCR/updateMspData',
          'createCR/updateOtherEquipmentData',
          'createCR/updateAttachmentsRailsData',
          'createCR/updateDocumentationData'
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.timestamp'],
        // Ignore these paths in the state
        ignoredPaths: ['database.lastFetched'],
      },
    }).concat(createFormPersistenceMiddleware()),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

